import { useMemo } from 'react'
import { LayerId, Layer, SymbolMaster } from 'modules/shares/types'
import { InspectorState } from 'modules/shares/Inspector/types'
import { getSymbolLayer, getSymbolData } from './Symbol.utils'
import { useVersioning } from 'modules/versioning'
import {
  useGetArtboardInDocumentWithUuidQuery,
  GetArtboardInDocumentWithUuidQuery,
  useShareForDocumentWithUuidQuery,
  VersionFragment,
} from 'gqlTypes'

export interface UseSymbolProps {
  inspectorData?: InspectorState['data']
  selectedLayerId?: LayerId
  currentVersion?: VersionFragment
}

export interface UseSymbol {
  symbolLayer: Layer | null
  symbolData: SymbolMaster | null
  symbolArtboard: Pick<
    NonNullable<
      GetArtboardInDocumentWithUuidQuery['artboardInDocumentWithUuid']
    >,
    '__typename' | 'permanentArtboardShortId' | 'isSymbolMaster'
  > | null
  foreignSymbolShareId?: string
  foreignVersionShortId?: string
}

export const useSymbol = ({
  inspectorData,
  selectedLayerId,
  currentVersion,
}: UseSymbolProps): UseSymbol => {
  const { latestVersion } = useVersioning()

  // Identify top-level symbol instance layer
  const symbolLayer = useMemo(() => {
    const layers = inspectorData?.layers

    if (!layers) {
      return null
    }

    const selectedLayer = layers?.[selectedLayerId as string]

    return getSymbolLayer(layers, selectedLayer)
  }, [inspectorData, selectedLayerId])

  // Get symbol data from `referencedSymbols` in `inspectorData`
  const symbolData = useMemo(() => {
    if (!symbolLayer?.symbolMasterID) {
      return null
    }
    return getSymbolData(inspectorData, symbolLayer.symbolMasterID as string)
  }, [inspectorData, symbolLayer])

  // To get the foreign symbol document ID, we need to query for:
  // Share -> version -> document -> identifier
  //
  // To get the share ID for foreign symbols, call needs to be
  // network-only, otherwise when switching between artboards,
  // we could get results from the previous board
  const foreignSymbolShareResponse = useShareForDocumentWithUuidQuery({
    variables: {
      name: symbolData?.documentName ?? '',
      uuid: symbolData?.documentID ?? '',
    },
    skip: !symbolData?.documentName || !symbolData?.documentID,
    fetchPolicy: 'network-only',
  })

  const foreignSymbolShare =
    foreignSymbolShareResponse?.data?.shareForDocumentWithUuid

  const internalDocumentId =
    currentVersion?.document?.identifier || latestVersion.document?.identifier

  // When symbolData.documentID is null means that the symbol is internal,
  // but for convenience we could use also symbolData.isForeign
  const documentId = symbolData?.isForeign
    ? foreignSymbolShare?.version?.document?.identifier
    : internalDocumentId

  // Artboard query needs to be done to both internal and external symbols.
  // UUIDs are not uniques, that's why we need also documentId
  //
  // TODO: depending on the code error we know if the artboard doesnt exist (Code: NOT_FOUND)
  // or if the user doesnt have access to the artboard (Code: UNAUTHORIZED)
  const symbolArtboardResponse = useGetArtboardInDocumentWithUuidQuery({
    variables: {
      documentId: documentId ?? '',
      artboardUuid: symbolData?.id as string,
    },
    // if any of the variables aren't valid we skip the query
    skip: !!(!documentId || !symbolData?.id),
    // call needs to be network-only, otherwise when switching between artboards,
    // we could get results from the previous board
    fetchPolicy: 'network-only',
  })

  const loading =
    foreignSymbolShareResponse.loading || symbolArtboardResponse.loading

  // Avoid returning partial data
  if (loading) {
    return {
      symbolLayer: null,
      symbolData: null,
      symbolArtboard: null,
      foreignSymbolShareId: undefined,
      foreignVersionShortId: undefined,
    }
  }

  return {
    symbolLayer,
    symbolData,
    symbolArtboard:
      symbolArtboardResponse.data?.artboardInDocumentWithUuid || null,
    foreignSymbolShareId: foreignSymbolShare?.identifier,
    foreignVersionShortId: foreignSymbolShare?.version?.shortId,
  }
}
